'use client';

import clsx from 'clsx';
import type { FC, PropsWithChildren } from 'react';
import { useRef, useState, useEffect } from 'react';

import styles from './styles.module.css';

interface TickerTapeProps extends PropsWithChildren {
	className?: string;
	delay?: number;
	direction?: 'alternate-reverse' | 'alternate' | 'normal' | 'reverse';
	loop?: boolean;
	speed?: number;
}

// Pure utility function - no state changes
const checkOverflow = (
	containerElement: HTMLDivElement | null,
	contentElement: HTMLDivElement | null,
) => {
	if (!containerElement || !contentElement) {
		return false;
	}
	const containerWidth = containerElement.clientWidth;
	const contentWidth = contentElement.scrollWidth;
	const isOverflowing = contentWidth > containerWidth;
	return isOverflowing;
};

export const TickerTape: FC<TickerTapeProps> = ({
	children,
	className,
	delay = 1,
	direction = 'normal',
	loop = true,
	speed = 30,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const [shouldAnimate, setShouldAnimate] = useState(false);

	useEffect(() => {
		const isOverflowing = checkOverflow(
			containerRef.current,
			contentRef.current,
		);
		setShouldAnimate(isOverflowing);
	}, []);

	let animationIterationCount: number | string;
	if (shouldAnimate) {
		animationIterationCount = loop ? 'infinite' : 1;
	} else {
		animationIterationCount = 'none';
	}

	const cx = clsx(styles.tickerTape, className, {
		[styles.tickerTapeMarquee]: shouldAnimate,
	});

	return (
		<div
			aria-atomic="true"
			aria-live="polite"
			className={cx}
			ref={containerRef}
		>
			<div
				className={styles.tickerTapeContent}
				ref={contentRef}
				style={{
					animationDelay: shouldAnimate ? `${delay}s` : '0s',
					animationDirection: direction,
					animationDuration: shouldAnimate ? `${speed}s` : 'none',
					animationIterationCount,
					animationName: shouldAnimate ? styles.slide : 'none',
					animationTimingFunction: shouldAnimate ? 'linear' : 'none',
				}}
			>
				{Array.from({ length: shouldAnimate ? 2 : 1 }, (_, index) => {
					return (
						<div className={styles.tickerTapeItem} key={index}>
							{children}
						</div>
					);
				})}
			</div>
		</div>
	);
};
